import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet, Package, AlertTriangle, Download, ShieldCheck } from "lucide-react";
import { formatMoney } from "@/lib/currency";

// Schedule C-aligned expense categories. Display labels are quiet-luxury lowercase.
const CATEGORIES = [
  { key: "advertising", label: "advertising" },
  { key: "car_mileage", label: "car / mileage" },
  { key: "commissions_fees", label: "commissions & fees" },
  { key: "contract_labor", label: "contract labor" },
  { key: "office_expenses", label: "office expenses" },
  { key: "supplies", label: "supplies" },
  { key: "postage_shipping", label: "postage / shipping" },
  { key: "software_subscriptions", label: "software & subscriptions" },
  { key: "repairs_maintenance", label: "repairs & maintenance" },
  { key: "home_office", label: "home office" },
  { key: "other", label: "other" },
];

const TABS = [
  { id: "dashboard", label: "dashboard" },
  { id: "sales", label: "sales" },
  { id: "expenses", label: "expenses" },
  { id: "sales_tax", label: "sales tax" },
  { id: "mileage", label: "mileage" },
  { id: "home_office", label: "home office" },
  { id: "evidence", label: "evidence vault" },
  { id: "reports", label: "reports" },
  { id: "compliance", label: "compliance" },
];

const fmt = (n) => formatMoney(n || 0);
const today = () => new Date().toISOString().slice(0, 10);

// ---------------- Disclaimer banner ----------------
function CpaDisclaimer() {
  return (
    <div className="border border-amber-700/40 bg-amber-50/60 px-5 py-3 mb-8 flex items-start gap-3" data-testid="cpa-disclaimer">
      <AlertTriangle className="w-4 h-4 text-amber-800 shrink-0 mt-0.5" />
      <p className="text-xs text-espresso leading-relaxed">
        <span className="font-medium text-amber-900">Recordkeeping aid — not tax advice.</span>{" "}
        Confirm all positions (home office eligibility, profit-motive substantiation under IRC §183, sales tax nexus, marketplace facilitator rules)
        with a Georgia-licensed CPA before filing. Default rates and reminders reflect general guidance and may not match your specific situation.
      </p>
    </div>
  );
}

// ---------------- Dashboard ----------------
function DashboardTab({ summary }) {
  const inv = summary.inventory;
  const sl = summary.sales;
  const op = summary.operating;
  const cashFlow = (sl.net_to_house || 0) - (op.expenses_total || 0) - (sl.payment_processing_fees || 0) - (sl.shipping_label_costs || 0);
  const cards = [
    { label: "gross sales", value: fmt(sl.gross_revenue), icon: TrendingUp, tone: "text-charcoal" },
    { label: "projected revenue", value: fmt(inv.projected_revenue_unsold), icon: TrendingUp, tone: "text-charcoal", note: "if all unsold pieces sell at list" },
    { label: "net profit", value: fmt(op.net_profit), icon: op.net_profit >= 0 ? TrendingUp : TrendingDown, tone: op.net_profit >= 0 ? "text-emerald-800" : "text-red-800" },
    { label: "inventory value", value: fmt(inv.cost_basis_unsold), icon: Package, tone: "text-charcoal", note: `${inv.unsold_pieces} pieces` },
    { label: "cogs (sold)", value: fmt(sl.cogs), icon: Package, tone: "text-charcoal" },
    { label: "operating expenses", value: fmt(op.expenses_total), icon: Wallet, tone: "text-charcoal" },
    { label: "sales tax owed", value: fmt(sl.tax_owed_to_state), icon: ShieldCheck, tone: sl.tax_owed_to_state > 0 ? "text-amber-800" : "text-charcoal" },
    { label: "cash flow", value: fmt(cashFlow), icon: Wallet, tone: cashFlow >= 0 ? "text-emerald-800" : "text-red-800" },
  ];
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="kpi-cards">
        {cards.map((c) => (
          <div key={c.label} className="bg-ivory border border-taupe/50 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="editorial-label">{c.label}</span>
              <c.icon className={`w-4 h-4 ${c.tone}`} />
            </div>
            <p className={`font-serif text-3xl ${c.tone}`}>{c.value}</p>
            {c.note && <p className="text-xs text-taupe mt-1">{c.note}</p>}
          </div>
        ))}
      </div>

      {/* P&L breakdown */}
      <div className="bg-ivory border border-taupe/50 p-7">
        <h2 className="editorial-label mb-5">profit & loss · year to date</h2>
        <div className="space-y-2 max-w-xl">
          {[
            ["gross sales", sl.gross_revenue],
            ["– platform fees", -(sl.platform_fees || 0)],
            ["– payment processing", -(sl.payment_processing_fees || 0)],
            ["– shipping labels", -(sl.shipping_label_costs || 0)],
            ["= net to house", sl.net_to_house - (sl.payment_processing_fees || 0) - (sl.shipping_label_costs || 0), "border-t"],
            ["– cost of goods sold", -(sl.cogs || 0)],
            ["= gross profit", sl.gross_profit - (sl.payment_processing_fees || 0) - (sl.shipping_label_costs || 0), "border-t font-medium"],
            ["– operating expenses", -(op.expenses_total || 0)],
            ["= net profit", op.net_profit, "border-t-2 font-medium"],
          ].map(([label, value, cls]) => (
            <div key={label} className={`flex justify-between py-2 ${cls || ""} border-taupe/30`}>
              <span className="text-espresso">{label}</span>
              <span className={`font-serif text-lg ${value < 0 ? "text-red-800" : "text-charcoal"}`}>{fmt(value)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Sales tab ----------------
function SalesTab({ sales, onUpdate }) {
  const [editing, setEditing] = useState(null);
  const save = async (id, form) => {
    await api.put(`/inventory/${id}/sale_detail`, form);
    toast.success("sale detail saved.");
    setEditing(null);
    onUpdate();
  };
  return (
    <div className="bg-ivory border border-taupe/50 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-taupe/30 text-left">
            {["date", "sku", "title", "channel", "gross", "fees", "label", "tax", "refunded", ""].map((h) => (
              <th key={h} className="px-4 py-3 editorial-label text-[0.6rem]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sales.length === 0 && <tr><td colSpan={10} className="text-center text-taupe py-8">no sales yet.</td></tr>}
          {sales.map((s) => (
            <tr key={s.id} className={`border-b border-taupe/15 hover:bg-linen/40 ${s.sale_refunded ? "opacity-50" : ""}`} data-testid={`sale-row-${s.sku}`}>
              <td className="px-4 py-3">{(s.sold_at || "").slice(0, 10)}</td>
              <td className="px-4 py-3 font-mono text-xs">{s.sku}</td>
              <td className="px-4 py-3">{s.title}</td>
              <td className="px-4 py-3 text-taupe">{s.sold_channel || "—"}</td>
              <td className="px-4 py-3 font-serif">{fmt(s.sold_price || s.sale_price)}</td>
              <td className="px-4 py-3 text-taupe">{fmt((s.sold_price || s.sale_price) * (s.platform_fee_percentage || 0))}</td>
              <td className="px-4 py-3 text-taupe">{fmt(s.sale_shipping_label_cost)}</td>
              <td className="px-4 py-3 text-taupe">{fmt(s.sale_tax_collected)}{s.sale_tax_remitted_by_marketplace ? " · mp" : ""}</td>
              <td className="px-4 py-3 text-xs">{s.sale_refunded ? "yes" : ""}</td>
              <td className="px-4 py-3">
                <button onClick={() => setEditing(s)} className="text-xs underline text-brass" data-testid={`sale-edit-${s.sku}`}>edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && <SaleDetailDialog sale={editing} onClose={() => setEditing(null)} onSave={(form) => save(editing.id, form)} />}
    </div>
  );
}

function SaleDetailDialog({ sale, onClose, onSave }) {
  const [f, setF] = useState({
    sale_buyer_paid_shipping: sale.sale_buyer_paid_shipping || 0,
    sale_discount: sale.sale_discount || 0,
    sale_payment_processing_fee: sale.sale_payment_processing_fee || 0,
    sale_shipping_label_cost: sale.sale_shipping_label_cost || 0,
    sale_tax_collected: sale.sale_tax_collected || 0,
    sale_tax_remitted_by_marketplace: sale.sale_tax_remitted_by_marketplace || false,
    sale_jurisdiction: sale.sale_jurisdiction || "GA-Cobb",
    sale_refunded: sale.sale_refunded || false,
    sale_refund_amount: sale.sale_refund_amount || 0,
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ivory border border-taupe/50 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
        <p className="editorial-label">sale detail</p>
        <h3 className="font-serif text-2xl lowercase text-charcoal mb-5">{sale.title}</h3>
        <div className="grid grid-cols-2 gap-4">
          {[
            ["sale_buyer_paid_shipping", "buyer-paid shipping"],
            ["sale_discount", "discount"],
            ["sale_payment_processing_fee", "payment processing fee"],
            ["sale_shipping_label_cost", "shipping label cost"],
            ["sale_tax_collected", "tax collected"],
            ["sale_refund_amount", "refund amount"],
          ].map(([k, label]) => (
            <label key={k} className="flex flex-col">
              <span className="editorial-label mb-1">{label}</span>
              <input type="number" step="0.01" value={f[k]} onChange={(e) => set(k, parseFloat(e.target.value || 0))} className="border border-taupe/40 px-3 py-2 bg-white" data-testid={`sd-${k}`} />
            </label>
          ))}
          <label className="flex flex-col">
            <span className="editorial-label mb-1">jurisdiction</span>
            <input type="text" value={f.sale_jurisdiction} onChange={(e) => set("sale_jurisdiction", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" placeholder="GA-Cobb" />
          </label>
        </div>
        <div className="flex items-center gap-6 mt-5">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.sale_tax_remitted_by_marketplace} onChange={(e) => set("sale_tax_remitted_by_marketplace", e.target.checked)} data-testid="sd-marketplace-remitted" />
            tax remitted by marketplace
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.sale_refunded} onChange={(e) => set("sale_refunded", e.target.checked)} data-testid="sd-refunded" />
            refunded
          </label>
        </div>
        <div className="flex gap-3 justify-end mt-7">
          <button onClick={onClose} className="px-4 py-2 border border-taupe text-xs uppercase tracking-widest">cancel</button>
          <button onClick={() => onSave(f)} className="px-4 py-2 bg-charcoal text-ivory text-xs uppercase tracking-widest" data-testid="sd-save">save</button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Expenses ----------------
function ExpensesTab({ expenses, onChange }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const remove = async (id) => {
    if (!window.confirm("remove this expense?")) return;
    await api.delete(`/accounting/expenses/${id}`);
    toast.success("expense removed.");
    onChange();
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setShow(true)} className="flex items-center gap-2 bg-charcoal text-ivory px-4 py-2 text-xs uppercase tracking-widest" data-testid="add-expense">
          <Plus className="w-4 h-4" /> log expense
        </button>
      </div>
      <div className="bg-ivory border border-taupe/50 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-taupe/30 text-left">
              {["date", "label", "category", "amount", "receipt", ""].map((h) => <th key={h} className="px-4 py-3 editorial-label text-[0.6rem]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 && <tr><td colSpan={6} className="text-center text-taupe py-8">no expenses logged.</td></tr>}
            {expenses.map((e) => (
              <tr key={e.id} className="border-b border-taupe/15">
                <td className="px-4 py-3">{e.incurred_on}</td>
                <td className="px-4 py-3">{e.label}</td>
                <td className="px-4 py-3 text-taupe text-xs">{(CATEGORIES.find((c) => c.key === e.category) || {}).label || e.category}</td>
                <td className="px-4 py-3 font-serif">{fmt(e.amount)}</td>
                <td className="px-4 py-3 text-xs">{e.receipt_path ? <span className="text-emerald-800">✓ attached</span> : <span className="text-taupe">—</span>}</td>
                <td className="px-4 py-3 flex gap-3">
                  <button onClick={() => setEditing(e)} className="text-xs underline text-brass">edit</button>
                  <button onClick={() => remove(e.id)} className="text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && <ExpenseDialog onClose={() => setShow(false)} onSaved={() => { setShow(false); onChange(); }} />}
      {editing && <ExpenseDialog expense={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChange(); }} />}
    </div>
  );
}

function ExpenseDialog({ expense, onClose, onSaved }) {
  const isEdit = !!expense;
  const [f, setF] = useState(expense ? { ...expense } : { label: "", category: "supplies", amount: 0, incurred_on: today(), notes: "", receipt_path: null });
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("receipt_path", data.path);
      toast.success("receipt attached.");
    } catch { toast.error("upload failed."); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!f.label || !f.amount) { toast.error("label and amount required."); return; }
    try {
      if (isEdit) await api.put(`/accounting/expenses/${expense.id}`, f);
      else await api.post("/accounting/expenses", f);
      toast.success(isEdit ? "expense updated." : "expense logged.");
      onSaved();
    } catch { toast.error("save failed."); }
  };
  return (
    <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ivory border border-taupe/50 max-w-lg w-full max-h-[90vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
        <p className="editorial-label">{isEdit ? "edit expense" : "log expense"}</p>
        <h3 className="font-serif text-2xl lowercase text-charcoal mb-5">schedule c entry</h3>
        <div className="space-y-4">
          <label className="flex flex-col">
            <span className="editorial-label mb-1">label</span>
            <input value={f.label} onChange={(e) => set("label", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="exp-label" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col">
              <span className="editorial-label mb-1">category</span>
              <select value={f.category} onChange={(e) => set("category", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="exp-category">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col">
              <span className="editorial-label mb-1">amount</span>
              <input type="number" step="0.01" value={f.amount} onChange={(e) => set("amount", parseFloat(e.target.value || 0))} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="exp-amount" />
            </label>
          </div>
          <label className="flex flex-col">
            <span className="editorial-label mb-1">incurred on</span>
            <input type="date" value={f.incurred_on} onChange={(e) => set("incurred_on", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" />
          </label>
          <label className="flex flex-col">
            <span className="editorial-label mb-1">notes</span>
            <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="border border-taupe/40 px-3 py-2 bg-white" />
          </label>
          <label className="flex flex-col">
            <span className="editorial-label mb-1">receipt</span>
            {f.receipt_path ? (
              <div className="flex items-center gap-3 text-xs">
                <span className="text-emerald-800">✓ attached</span>
                <button onClick={() => set("receipt_path", null)} className="text-red-700 underline">remove</button>
              </div>
            ) : (
              <label className="border border-taupe/40 px-3 py-2 bg-white cursor-pointer text-xs uppercase tracking-widest text-center">
                {uploading ? "uploading…" : "upload receipt"}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
              </label>
            )}
          </label>
        </div>
        <div className="flex gap-3 justify-end mt-7">
          <button onClick={onClose} className="px-4 py-2 border border-taupe text-xs uppercase tracking-widest">cancel</button>
          <button onClick={save} className="px-4 py-2 bg-charcoal text-ivory text-xs uppercase tracking-widest" data-testid="exp-save">save</button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Sales Tax tab ----------------
function SalesTaxTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/accounting/reports/sales_tax").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="editorial-label">loading…</p>;
  const totals = data.rows.reduce((a, r) => ({ col: a.col + r.tax_collected, mp: a.mp + r.tax_remitted_by_marketplace, ow: a.ow + r.tax_owed_to_state }), { col: 0, mp: 0, ow: 0 });
  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Stat label="collected (ytd)" value={fmt(totals.col)} />
        <Stat label="remitted by marketplace" value={fmt(totals.mp)} />
        <Stat label="owed to Georgia" value={fmt(totals.ow)} tone={totals.ow > 0 ? "amber" : ""} />
      </div>
      <div className="bg-ivory border border-taupe/50 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-taupe/30 text-left">
              {["month", "collected", "marketplace-remitted", "owed to state", "direct sales"].map((h) => <th key={h} className="px-4 py-3 editorial-label text-[0.6rem]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.month} className="border-b border-taupe/15">
                <td className="px-4 py-3 font-mono text-xs">{r.month}</td>
                <td className="px-4 py-3 font-serif">{fmt(r.tax_collected)}</td>
                <td className="px-4 py-3 text-taupe">{fmt(r.tax_remitted_by_marketplace)}</td>
                <td className={`px-4 py-3 font-serif ${r.tax_owed_to_state > 0 ? "text-amber-800" : "text-charcoal"}`}>{fmt(r.tax_owed_to_state)}</td>
                <td className="px-4 py-3 text-taupe">{r.direct_sales_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Mileage ----------------
function MileageTab() {
  const [trips, setTrips] = useState([]);
  const [show, setShow] = useState(false);
  const load = () => api.get("/accounting/mileage").then((r) => setTrips(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("remove this trip?")) return;
    await api.delete(`/accounting/mileage/${id}`);
    load();
  };
  const totalMiles = trips.reduce((a, t) => a + (t.miles || 0), 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="editorial-label">total miles ytd</p>
          <p className="font-serif text-3xl text-charcoal">{totalMiles.toFixed(1)}</p>
        </div>
        <button onClick={() => setShow(true)} className="flex items-center gap-2 bg-charcoal text-ivory px-4 py-2 text-xs uppercase tracking-widest" data-testid="add-trip">
          <Plus className="w-4 h-4" /> log trip
        </button>
      </div>
      <div className="bg-ivory border border-taupe/50 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-taupe/30 text-left">
              {["date", "purpose", "from", "to", "miles", "notes", ""].map((h) => <th key={h} className="px-4 py-3 editorial-label text-[0.6rem]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {trips.length === 0 && <tr><td colSpan={7} className="text-center text-taupe py-8">no trips logged.</td></tr>}
            {trips.map((t) => (
              <tr key={t.id} className="border-b border-taupe/15">
                <td className="px-4 py-3">{t.date}</td>
                <td className="px-4 py-3">{t.purpose}</td>
                <td className="px-4 py-3 text-taupe">{t.start_location}</td>
                <td className="px-4 py-3 text-taupe">{t.destination}</td>
                <td className="px-4 py-3 font-serif">{(t.miles || 0).toFixed(1)}</td>
                <td className="px-4 py-3 text-xs text-taupe">{t.notes}</td>
                <td className="px-4 py-3"><button onClick={() => remove(t.id)} className="text-red-700"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && <MileageDialog onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function MileageDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ date: today(), purpose: "", start_location: "home", destination: "", miles: 0, notes: "" });
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    if (!f.purpose || !f.miles) { toast.error("purpose & miles required."); return; }
    await api.post("/accounting/mileage", f);
    toast.success("trip logged.");
    onSaved();
  };
  return (
    <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ivory border border-taupe/50 max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
        <p className="editorial-label">log trip</p>
        <h3 className="font-serif text-2xl lowercase text-charcoal mb-5">mileage entry</h3>
        <div className="space-y-3">
          {["date", "purpose", "start_location", "destination", "notes"].map((k) => (
            <label key={k} className="flex flex-col">
              <span className="editorial-label mb-1">{k.replace("_", " ")}</span>
              <input type={k === "date" ? "date" : "text"} value={f[k]} onChange={(e) => set(k, e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid={`trip-${k}`} />
            </label>
          ))}
          <label className="flex flex-col">
            <span className="editorial-label mb-1">miles</span>
            <input type="number" step="0.1" value={f.miles} onChange={(e) => set("miles", parseFloat(e.target.value || 0))} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="trip-miles" />
          </label>
        </div>
        <div className="flex gap-3 justify-end mt-7">
          <button onClick={onClose} className="px-4 py-2 border border-taupe text-xs uppercase tracking-widest">cancel</button>
          <button onClick={save} className="px-4 py-2 bg-charcoal text-ivory text-xs uppercase tracking-widest" data-testid="trip-save">save</button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Home Office ----------------
function HomeOfficeTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/accounting/reports/home_office").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="editorial-label">loading…</p>;
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="total home sqft" value={data.total_sqft} />
        <Stat label="business sqft" value={`${data.business_sqft} (${data.business_pct}%)`} />
        <Stat label="actual-method deduction" value={fmt(data.annual_deductible_actual_method)} />
        <Stat label="simplified-method deduction" value={fmt(data.annual_deductible_simplified_method)} />
      </div>
      <p className="text-xs text-espresso mb-4">
        edit sqft, monthly rent/mortgage, utilities, internet, insurance and repairs in <a href="/admin/settings" className="underline">institutional settings → home office</a>.
      </p>
      <div className="bg-amber-50/60 border border-amber-700/40 p-5 text-xs text-espresso">
        <p className="font-medium text-amber-900 mb-1">⚠ {data.disclaimer}</p>
      </div>
    </div>
  );
}

// ---------------- Evidence Vault ----------------
function EvidenceTab() {
  const [docs, setDocs] = useState([]);
  const [show, setShow] = useState(false);
  const load = () => api.get("/accounting/evidence").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("remove this document?")) return;
    await api.delete(`/accounting/evidence/${id}`);
    load();
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setShow(true)} className="flex items-center gap-2 bg-charcoal text-ivory px-4 py-2 text-xs uppercase tracking-widest" data-testid="add-evidence">
          <Plus className="w-4 h-4" /> upload document
        </button>
      </div>
      <div className="bg-ivory border border-taupe/50 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-taupe/30 text-left">
              {["uploaded", "kind", "label", "notes", ""].map((h) => <th key={h} className="px-4 py-3 editorial-label text-[0.6rem]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan={5} className="text-center text-taupe py-8">no evidence stored.</td></tr>}
            {docs.map((d) => (
              <tr key={d.id} className="border-b border-taupe/15">
                <td className="px-4 py-3 text-xs">{(d.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-xs uppercase tracking-widest">{d.kind}</td>
                <td className="px-4 py-3">{d.label}</td>
                <td className="px-4 py-3 text-xs text-taupe">{d.notes}</td>
                <td className="px-4 py-3"><button onClick={() => remove(d.id)} className="text-red-700"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && <EvidenceDialog onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function EvidenceDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ path: null, kind: "receipt", label: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("path", data.path);
    } catch { toast.error("upload failed."); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!f.path || !f.label) { toast.error("file + label required."); return; }
    await api.post("/accounting/evidence", f);
    toast.success("document filed.");
    onSaved();
  };
  return (
    <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ivory border border-taupe/50 max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
        <p className="editorial-label">upload document</p>
        <h3 className="font-serif text-2xl lowercase text-charcoal mb-5">evidence vault</h3>
        <div className="space-y-3">
          <label className="flex flex-col">
            <span className="editorial-label mb-1">kind</span>
            <select value={f.kind} onChange={(e) => set("kind", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white">
              <option value="receipt">receipt</option>
              <option value="license">business license</option>
              <option value="tax_certificate">tax certificate</option>
              <option value="st5_form">ST-5 form</option>
              <option value="other">other</option>
            </select>
          </label>
          <label className="flex flex-col">
            <span className="editorial-label mb-1">label</span>
            <input value={f.label} onChange={(e) => set("label", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="ev-label" />
          </label>
          <label className="flex flex-col">
            <span className="editorial-label mb-1">notes</span>
            <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="border border-taupe/40 px-3 py-2 bg-white" />
          </label>
          <label className="flex flex-col">
            <span className="editorial-label mb-1">file</span>
            {f.path ? (
              <div className="flex items-center gap-3 text-xs">
                <span className="text-emerald-800">✓ {f.path.split("/").pop()}</span>
                <button onClick={() => set("path", null)} className="text-red-700 underline">remove</button>
              </div>
            ) : (
              <label className="border border-taupe/40 px-3 py-2 bg-white cursor-pointer text-xs uppercase tracking-widest text-center">
                {uploading ? "uploading…" : "upload file"}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
              </label>
            )}
          </label>
        </div>
        <div className="flex gap-3 justify-end mt-7">
          <button onClick={onClose} className="px-4 py-2 border border-taupe text-xs uppercase tracking-widest">cancel</button>
          <button onClick={save} className="px-4 py-2 bg-charcoal text-ivory text-xs uppercase tracking-widest" data-testid="ev-save">save</button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Reports ----------------
function ReportsTab() {
  const [scheduleC, setScheduleC] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [activityEvidence, setActivityEvidence] = useState(null);
  const [inv, setInv] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/accounting/reports/schedule_c").then((r) => setScheduleC(r.data)),
      api.get("/accounting/reports/monthly_pnl").then((r) => setMonthly(r.data)),
      api.get("/accounting/reports/platform_breakdown").then((r) => setPlatform(r.data)),
      api.get("/accounting/reports/business_activity").then((r) => setActivityEvidence(r.data)),
      api.get("/accounting/reports/inventory_on_hand").then((r) => setInv(r.data)),
    ]).catch(() => {});
  }, []);
  const dl = (path, name) => {
    // CSV downloads via fetch with credentials so cookie auth works
    const url = `${api.defaults.baseURL}${path}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
  };
  return (
    <div className="space-y-8">
      {/* Quick exports */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ["/accounting/export/expenses.csv", "expenses.csv"],
          ["/accounting/export/sales.csv", "sales.csv"],
          ["/accounting/export/inventory.csv", "inventory.csv"],
          ["/accounting/export/mileage.csv", "mileage.csv"],
        ].map(([p, n]) => (
          <button key={n} onClick={() => dl(p, n)} className="border border-taupe hover:border-brass px-4 py-3 flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-charcoal bg-ivory" data-testid={`download-${n}`}>
            <Download className="w-3.5 h-3.5" /> {n}
          </button>
        ))}
      </div>

      {/* Schedule C summary */}
      {scheduleC && (
        <div className="bg-ivory border border-taupe/50 p-7">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="editorial-label">schedule c · {scheduleC.year}</p>
              <h3 className="font-serif text-2xl text-charcoal lowercase">net profit or loss → {fmt(scheduleC.lines["31_net_profit_or_loss"])}</h3>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-10 gap-y-2 text-sm max-w-3xl">
            {Object.entries(scheduleC.lines).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-taupe/15 py-1">
                <span className="text-espresso">{k.replace(/_/g, " ")}</span>
                <span className="font-serif">{fmt(v)}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-taupe mt-4 italic">{scheduleC.disclaimer}</p>
        </div>
      )}

      {/* Monthly P&L mini-table */}
      {monthly && (
        <div className="bg-ivory border border-taupe/50 p-7">
          <p className="editorial-label mb-3">monthly p&l · {monthly.year}</p>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-taupe/30 text-left">
              {["month", "gross", "fees", "cogs", "opex", "net", "sold"].map((h) => <th key={h} className="py-2 editorial-label text-[0.6rem]">{h}</th>)}
            </tr></thead>
            <tbody>
              {monthly.rows.map((r) => (
                <tr key={r.month} className="border-b border-taupe/15">
                  <td className="py-2 font-mono text-xs">{r.month}</td>
                  <td className="py-2 font-serif">{fmt(r.gross_revenue)}</td>
                  <td className="py-2 text-taupe">{fmt(r.platform_fees)}</td>
                  <td className="py-2 text-taupe">{fmt(r.cogs)}</td>
                  <td className="py-2 text-taupe">{fmt(r.operating_expenses)}</td>
                  <td className={`py-2 font-serif ${r.net_profit < 0 ? "text-red-800" : "text-emerald-800"}`}>{fmt(r.net_profit)}</td>
                  <td className="py-2 text-taupe">{r.sold_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Platform breakdown */}
      {platform && platform.rows.length > 0 && (
        <div className="bg-ivory border border-taupe/50 p-7">
          <p className="editorial-label mb-3">sales by platform · {platform.year}</p>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-taupe/30 text-left">{["channel", "sales", "gross", "fees", "shipping labels"].map((h) => <th key={h} className="py-2 editorial-label text-[0.6rem]">{h}</th>)}</tr></thead>
            <tbody>
              {platform.rows.map((r) => (
                <tr key={r.channel} className="border-b border-taupe/15">
                  <td className="py-2">{r.channel}</td>
                  <td className="py-2 text-taupe">{r.sales}</td>
                  <td className="py-2 font-serif">{fmt(r.gross)}</td>
                  <td className="py-2 text-taupe">{fmt(r.fees)}</td>
                  <td className="py-2 text-taupe">{fmt(r.shipping_labels)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Inventory on hand */}
      {inv && (
        <div className="bg-ivory border border-taupe/50 p-7">
          <p className="editorial-label mb-2">inventory on hand</p>
          <p className="text-sm text-espresso mb-3">
            {inv.piece_count} pieces · cost basis <span className="font-serif">{fmt(inv.total_cost_basis)}</span> · listed value <span className="font-serif">{fmt(inv.total_listed_value)}</span>
          </p>
          <button onClick={() => dl("/accounting/export/inventory.csv", "inventory.csv")} className="text-xs underline text-brass">download full csv</button>
        </div>
      )}

      {/* Business activity evidence — substantiates profit-motive */}
      {activityEvidence && (
        <div className="bg-ivory border border-taupe/50 p-7">
          <p className="editorial-label mb-3">business activity evidence · {activityEvidence.year}</p>
          <p className="text-xs text-espresso mb-4">documented proof of businesslike conduct — substantiating profit-motive under irc §183.</p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {[
              ["listings created", activityEvidence.listings_created],
              ["sales completed", activityEvidence.sales_completed],
              ["audit log entries", activityEvidence.audit_log_entries],
              ["expenses logged", activityEvidence.expenses_logged],
              ["mileage trips", activityEvidence.mileage_trips_logged],
              ["evidence documents", activityEvidence.evidence_documents],
              ["business license", activityEvidence.business_license_on_file ? "✓ on file" : "—"],
              ["GA sales tax acct", activityEvidence.ga_sales_tax_account ? "✓ on file" : "—"],
              ["EIN on file", activityEvidence.ein_on_file ? "✓ on file" : "—"],
            ].map(([k, v]) => (
              <div key={k} className="border border-taupe/20 p-3">
                <p className="editorial-label">{k}</p>
                <p className="font-serif text-lg text-charcoal">{v}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-espresso mt-3 italic">{activityEvidence.narrative}</p>
        </div>
      )}
    </div>
  );
}

// ---------------- Compliance ----------------
function ComplianceTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/accounting/compliance_reminders").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="editorial-label">loading…</p>;
  return (
    <div>
      <p className="text-sm text-espresso mb-5">upcoming filings and renewals · today is {data.today}</p>
      <div className="space-y-3">
        {data.items.map((it) => {
          const urgent = it.days_until_due <= 7;
          const soon = it.days_until_due <= 30;
          return (
            <div key={it.id} className={`bg-ivory border p-5 flex items-center justify-between ${urgent ? "border-red-700" : soon ? "border-amber-600" : "border-taupe/50"}`} data-testid={`reminder-${it.id}`}>
              <div>
                <p className="font-serif text-lg text-charcoal">{it.label}</p>
                <p className="text-xs text-taupe mt-1">{it.frequency} · {it.authority}</p>
              </div>
              <div className="text-right">
                <p className={`font-serif text-xl ${urgent ? "text-red-800" : soon ? "text-amber-800" : "text-charcoal"}`}>{it.due}</p>
                <p className="text-xs text-taupe">{it.days_until_due === 0 ? "today" : it.days_until_due < 0 ? `${-it.days_until_due} d overdue` : `in ${it.days_until_due} d`}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`bg-ivory border p-4 ${tone === "amber" ? "border-amber-700/50" : "border-taupe/50"}`}>
      <p className="editorial-label">{label}</p>
      <p className={`font-serif text-2xl ${tone === "amber" ? "text-amber-800" : "text-charcoal"}`}>{value}</p>
    </div>
  );
}

// ---------------- Root page ----------------
export default function AdminAccounting() {
  const [summary, setSummary] = useState(null);
  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const reload = async () => {
    const [s, sl, ex] = await Promise.all([
      api.get("/accounting/summary").then((r) => r.data),
      api.get("/accounting/sales").then((r) => r.data),
      api.get("/accounting/expenses").then((r) => r.data),
    ]);
    setSummary(s); setSales(sl); setExpenses(ex);
  };
  useEffect(() => { reload().catch(() => {}); }, []);
  if (!summary) return <AdminLayout><p className="editorial-label">loading…</p></AdminLayout>;
  return (
    <AdminLayout>
      <div data-testid="admin-accounting" className="max-w-7xl">
        <span className="editorial-label">finance · accounting & tax</span>
        <h1 className="font-serif text-5xl lowercase text-charcoal mt-3 mb-2">the house, in numbers.</h1>
        <p className="text-espresso max-w-2xl mb-8">cost basis, realised revenue, schedule c summary and tax obligations — the financial truth of maison pilet.</p>
        <CpaDisclaimer />
        <div className="flex flex-wrap gap-1 border-b border-taupe/30 mb-8" role="tablist" data-testid="accounting-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-xs uppercase tracking-widest ${tab === t.id ? "border-b-2 border-brass text-charcoal" : "text-taupe hover:text-charcoal"}`}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div role="tabpanel">
          {tab === "dashboard" && <DashboardTab summary={summary} />}
          {tab === "sales" && <SalesTab sales={sales} onUpdate={reload} />}
          {tab === "expenses" && <ExpensesTab expenses={expenses} onChange={reload} />}
          {tab === "sales_tax" && <SalesTaxTab />}
          {tab === "mileage" && <MileageTab />}
          {tab === "home_office" && <HomeOfficeTab />}
          {tab === "evidence" && <EvidenceTab />}
          {tab === "reports" && <ReportsTab />}
          {tab === "compliance" && <ComplianceTab />}
        </div>
      </div>
    </AdminLayout>
  );
}
